/**
 * DRBFM Processing Step Component
 *
 * Displays real-time progress of DRBFM analysis:
 * - Overall progress bar
 * - Individual flow status indicators
 * - Progress messages
 * - Cancel button
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { CircleX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DRBFMAnalysisProgress } from '../../../types';
import FlowProgressList from './processing/FlowProgressList';
import OverallProgressCard from './processing/OverallProgressCard';
import ProgressLogCard from './processing/ProgressLogCard';

interface DRBFMProcessingStepProps {
  progress: DRBFMAnalysisProgress;
  onCancel: () => void;
}

const DRBFMProcessingStep: React.FC<DRBFMProcessingStepProps> = ({
  progress,
  onCancel,
}) => {
  const { t } = useTranslation();

  const progressPercent = Math.round(progress.overallProgress * 100);

  return (
    <div>
      <div className="mb-6">
        <h2 className="revy-cjk mb-2 text-xl font-medium leading-[1.6]">
          {t('drbfm.processing.title')}
        </h2>
        <p className="revy-cjk text-sm leading-[1.5] text-muted-foreground">
          {t('drbfm.processing.description')}
        </p>
      </div>

      <OverallProgressCard progressPercent={progressPercent} error={progress.error} />

      <FlowProgressList
        flowStatuses={progress.flowStatuses}
        currentFlow={progress.currentFlow}
      />

      {progress.messages.length > 0 && <ProgressLogCard messages={progress.messages} />}

      <div className="flex justify-center">
        <Button
          variant="outline"
          onClick={onCancel}
          disabled={!progress.isRunning}
          className="border-destructive text-destructive hover:bg-destructive/10 hover:text-destructive"
        >
          <CircleX />
          <span className="revy-cjk">{t('drbfm.processing.cancel')}</span>
        </Button>
      </div>
    </div>
  );
};

export default DRBFMProcessingStep;
